/**
 * Finance Permission Utilities
 * 
 * Centralized permission checks for Finance features.
 * Used to gate Finance UI visibility and route access.
 * 
 * Permission Logic:
 * - FOUNDER: Always has finance access
 * - MANAGER with manager_finance_access=true: Has finance access
 * - STAFF: NEVER has finance access
 * - Everyone else: No finance access
 */

/**
 * Check if user has finance access
 * @param {Object} user - User object from AuthContext
 * @returns {boolean} - Whether user can access Finance features
 */
export function hasFinanceAccess(user) {
  if (!user) return false;
  
  // Founder always has access
  if (user.role === 'FOUNDER') return true;
  
  // Staff NEVER has access
  if (user.role === 'STAFF') return false;
  
  // Manager with finance_access permission
  if (user.role === 'MANAGER' && user.manager_finance_access === true) return true;
  
  // Everyone else - no access
  return false;
}

/**
 * Check if user is a Manager (used for role-specific UI)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export function isManager(user) {
  return user?.role === 'MANAGER';
}

/**
 * Check if user is Staff (most restricted role)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export function isStaff(user) {
  return user?.role === 'STAFF';
}

/**
 * Check if user is a Founder
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export function isFounderRole(user) {
  return user?.role === 'FOUNDER';
}

/**
 * Check if user can invite other users
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export function canInviteUsers(user) {
  if (!user) return false;
  
  // Founders can always invite
  if (user.role === 'FOUNDER') return true;
  
  // Managers can only invite if they have can_invite_staff permission
  if (user.role === 'MANAGER' && user.can_invite_staff === true) return true;
  
  // Staff and others cannot invite
  return false;
}

/**
 * Check if user can delete resources (jobs, quotes, invoices)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export function canDelete(user) {
  if (!user) return false;
  
  // Staff cannot delete
  if (user.role === 'STAFF') return false;
  
  // Founders and Managers can delete
  return user.role === 'FOUNDER' || user.role === 'MANAGER';
}

/**
 * Filter navigation items based on finance access
 * @param {Array} items - Navigation items array
 * @param {Object} user - User object from AuthContext
 * @returns {Array} - Filtered items
 */
export function filterNavItems(items, user) {
  const canAccessFinance = hasFinanceAccess(user);
  
  return items.filter(item => {
    // Finance-related paths
    const financePathPatterns = [
      '/finance',
      '/cashflow',
      '/margin',
      '/price-intelligence',
      '/xero',
      '/pots',
    ];
    
    const isFinanceItem = financePathPatterns.some(pattern => 
      item.path?.startsWith(pattern) || item.path?.includes('/finance')
    );
    
    // Hide finance items if user doesn't have access
    if (isFinanceItem && !canAccessFinance) {
      return false;
    }
    
    return true;
  });
}

/**
 * Filter navigation sections (removes empty sections after filtering)
 * @param {Array} sections - Navigation sections array
 * @param {Object} user - User object from AuthContext
 * @returns {Array} - Filtered sections
 */
export function filterNavSections(sections, user) {
  const canAccessFinance = hasFinanceAccess(user);
  
  return sections
    .map(section => ({
      ...section,
      items: filterNavItems(section.items, user)
    }))
    .filter(section => {
      // Remove entire Finance section if no access
      if (section.title === 'Finance' && !canAccessFinance) {
        return false;
      }
      // Remove sections with no items
      return section.items.length > 0;
    });
}

export default {
  hasFinanceAccess,
  isManager,
  isFounderRole,
  filterNavItems,
  filterNavSections
};
